/**
 * @function getMpnFromProductUrl
 * @description The MPN encoded in a product page slug. The two sites use mirror-image grammars,
 * so the brand's position relative to the MPN is per-site configuration:
 *
 *   exx, brand leading  `<brand>-<MPN>-E<digits>`
 *     `/Exxact-VWS-135223847-E135223847`     -> `VWS-135223847`
 *     `/Exxact-TS1-160859871-NVM-E160859871` -> `TS1-160859871-NVM`  (variant preserved)
 *     `/QNAP-TVS-H674-I3-16G-US-E5867057`    -> `TVS-H674-I3-16G-US`
 *
 *   spc, brand trailing  `<MPN>-<brand>-S<digits>`
 *     `/ES4-9889609-SabrePC-S9889609`        -> `ES4-9889609`
 *     `/TVS-H674-I3-16G-US-QNAP-S5867057`    -> `TVS-H674-I3-16G-US`
 *
 *     `/category/NVIDIA-DGX-Spark`           -> null                 (not a product page)
 *
 * Returns null when the url is not a product page — a landing page, a category page, or a
 * configurator link — so callers can treat that as "no configurator".
 *
 * The brand is one segment but not a fixed token: vendor products carry the vendor's name
 * (`QNAP`, `NVIDIA`, `Supermicro`) in that slot, so only its position is relied on. The MPN body
 * is deliberately not pattern-matched either — real MPNs carry extra segments
 * (`TS1-160859871-NVM`, `TVS-H674-I3-16G-US`) that any such pattern would strip or reject.
 *
 * @param {string} url a CMS link's routerLink, or any product url/slug
 * @param {string} productUrlRegex environment.productUrlRegex ('-E[0-9]{6,9}$' | '-S[0-9]{6,9}$')
 * @param {string} brandPosition environment.productUrlBrandPosition ('leading' | 'trailing')
 * @returns {string} the MPN, or null when the url is not a product page
 */
export function getMpnFromProductUrl(url: string, productUrlRegex: string, brandPosition?: string): string {
    if (!url || !productUrlRegex) {
        return null;
    }
    const path = url.split('?')[0].split('#')[0];
    const slug = path.substring(path.lastIndexOf('/') + 1);
    // The lazy capture plus the $-anchored lookahead pins the MPN between the brand and the
    // suffix, so interior segments survive: TVS-H674-I3-16G-US-QNAP-S5867057 keeps its dashes.
    const pattern =
        brandPosition === 'trailing'
            ? `^(.+?)(?=-[A-Za-z0-9]+${productUrlRegex})`
            : `^[A-Za-z0-9]+-(.+?)(?=${productUrlRegex})`;
    const match = slug.match(new RegExp(pattern));
    return match ? match[1].toUpperCase() : null;
}

/**
 * @function resolveDisplayMpn
 * @description The MPN to show on the card and send with a quote: the editor's override when
 * they set one, else the MPN from the url, else whatever was authored.
 *
 * `useCustomMpn` is honored verbatim, blank included — ticking it with an empty Part Number
 * returns '' and the card shows no MPN.
 *
 * @param {string} urlMpn the MPN from the solution page url, or null
 * @param {string} authoredMpn the hand-entered part_number
 * @param {boolean} useCustomMpn the Use Custom Part Number toggle
 * @returns {string} the MPN to display, or '' to show none
 */
export function resolveDisplayMpn(urlMpn: string, authoredMpn: string, useCustomMpn?: boolean): string {
    if (useCustomMpn) {
        return authoredMpn || '';
    }
    return urlMpn || authoredMpn || '';
}

/**
 * @function resolveConfiguratorMpn
 * @description The MPN that drives the configuration lookups and the Configure href. A ticked
 * override wins outright and the url MPN is not consulted, so an authored MPN the configurator
 * does not know correctly yields no Configure button rather than quietly linking elsewhere.
 *
 * Unlike resolveDisplayMpn there is no authored fallback when unticked: a url that is not a
 * product page means there is no configurator, so the button must not render.
 *
 * @param {string} urlMpn the MPN from the solution page url, or null
 * @param {string} authoredMpn the hand-entered part_number
 * @param {boolean} useCustomMpn the Use Custom Part Number toggle
 * @returns {string} the MPN to configure, or a falsy value when there is no configurator
 */
export function resolveConfiguratorMpn(urlMpn: string, authoredMpn: string, useCustomMpn?: boolean): string {
    return useCustomMpn ? authoredMpn || '' : urlMpn;
}

/**
 * @function getMpnLookupCandidates
 * @description MPN candidates to try against the configuration API, most specific first: each
 * supplied MPN followed by its base form (the first two dash segments). Deduped, blanks dropped.
 *
 * Called with the configurator MPN, so a variant such as `TS1-160859871-NVM` also tries its
 * parent `TS1-160859871`. Accepts several MPNs for callers that have a priority order.
 *
 * @param {...string} mpns MPNs in priority order
 * @returns {string[]} candidates to try in order
 */
export function getMpnLookupCandidates(...mpns: string[]): string[] {
    const candidates: string[] = [];
    for (const mpn of mpns) {
        if (!mpn) {
            continue;
        }
        candidates.push(mpn);
        const base = mpn.split('-').slice(0, 2).join('-');
        if (base !== mpn) {
            candidates.push(base);
        }
    }
    return candidates.filter((mpn, i) => candidates.indexOf(mpn) === i);
}
